import { useState } from 'react';
import { Flex } from '@chakra-ui/react';

import {
  IEditorialActionReference,
  IEditorialPhase,
  IWorkflowStateMeta
} from '@publishing-engine/domain/models';
import { getActionLabelAndDescription } from 'src/utils/displayHelpers';
import AddNoteModal from 'src/components/SubmissionDetailsSidebar/AddNoteModal';
import ContextMenu from 'src/components/Submission/ContextMenu';
import { ValueOf } from 'src/utils/types';
import { IGetArticleWorkflowResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { useSubmissionData } from '../../contexts/SubmissionDataProvider.js';
import { ISubmissionDomainActionReference } from '@publishing-engine/domain/actions/v1/commands';
import PrepareResponseToAuthorModal from 'src/components/SubmissionDetailsSidebar/PrepareResponseToAuthorModal';
import { SemanticTokenColor } from '@morressier/alembic/_dist/cjs/types';

import ExportSubmissionModal from '../../components/SubmissionDetailsSidebar/ExportSubmissionModal.js';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider.js';

type WorkflowStateConfig = ValueOf<
  IGetArticleWorkflowResponse['articleWorkflow']['transitionGraph']['submission']['states']
>;

type Props = {
  phase: IWorkflowStateMeta;
  workflowStateConfig: WorkflowStateConfig;
  showingDecisionsForPhase: null | string;
  setShowingDecisionsForPhase: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  setShowingDecisionModal: (action: string) => void;
  isCurrentOwner?: boolean;
};

type IContextMenuActionEntry = {
  accessLevel: 'task_completer' | 'decision_maker' | 'viewer';
  name: ISubmissionDomainActionReference | string;
  actionReferenceName?: ISubmissionDomainActionReference;
  headingColor?: SemanticTokenColor;
  action: () => void;
  description?: string;
};

export const EditorialWorkPhaseActions = ({
  phase,
  workflowStateConfig,
  showingDecisionsForPhase,
  setShowingDecisionModal
}: Props) => {
  const { workflowResponse, isTaskCompleter, isDecisionMaker } =
    useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const submissionId = workflowResponse.submissionId;
  const submissionContextId = workflowResponse.submissionContextId;

  const phaseName = phase.name as IEditorialPhase;
  const { actions } = workflowStateConfig;

  const isShowingDecisionsOfOtherPhase =
    showingDecisionsForPhase && showingDecisionsForPhase !== phaseName;

  const [showAddNoteModal, setShowAddNoteModal] = useState<string | null>(null);
  const [isPrepareResponseModalOpen, setPrepareResponseModalOpen] =
    useState(false);

  const [showExportSubmissionModal, setExportSubmissionModal] = useState(false);

  if (isShowingDecisionsOfOtherPhase) {
    return <></>;
  }

  const actionsByDisplay = Object.groupBy(
    actions,
    action => action.displayProperties.location
  );

  const contextActions = actionsByDisplay.contextMenu ?? [];
  const isCOCEnabled = (submissionContext?.config.cocConfig?.length ?? 0) !== 0;

  const contextMenuActions = contextActions.reduce(
    (acc, action) => {
      if (
        action.name === 'RequestAdjudication' &&
        !submissionContext?.config.isAdjudicationEnabled
      ) {
        return acc;
      }
      if (
        action.name === 'RequestEarlyAssessment' &&
        !submissionContext?.config.isEarlyAssessmentEnabled
      ) {
        return acc;
      }

      const editorialAction = action as IEditorialActionReference;
      if (editorialAction.accessLevel === 'decision_maker') {
        const showDecisionMakerContextAction =
          action.name === 'RevertDecision'
            ? isDecisionMaker
            : isDecisionMaker && !isCOCEnabled;
        if (!showDecisionMakerContextAction) {
          return acc;
        }
      } else if (
        editorialAction.accessLevel === 'task_completer' &&
        !isTaskCompleter
      ) {
        return acc;
      }

      const actionData = getActionLabelAndDescription(action.name);
      return [
        ...acc,
        {
          accessLevel: (action as IEditorialActionReference).accessLevel,
          name: actionData?.label || action.name,
          description: actionData?.description,
          headingColor: actionData?.headingColor,
          action: () => setShowingDecisionModal(action.name)
        }
      ];
    },
    [
      // Commenting for This Feature not required as of now
      // ...(isTaskCompleter && isPeerReviewPhase && !isRecommendationMaker
      //   ? [
      //       {
      //         name: 'Prepare Response to Author',
      //         accessLevel: 'task_completer',
      //         action: () => setPrepareResponseModalOpen(true)
      //       }
      //     ]
      //   : []),

      // Commenting for Vendor Exports
      // ...(phaseName === 'preProduction' || phaseName === 'formalAcceptance'
      //   ? [
      //       {
      //         name: 'Export Submission Data',
      //         accessLevel: 'viewer',
      //         action: () => setExportSubmissionModal(true),
      //         description: `Quickly export this submission's data to speed up your work before formal acceptance.`
      //       }
      //     ]
      //   : []),
      {
        name: 'Add General Note',
        accessLevel: 'viewer',
        action: () => setShowAddNoteModal('general'),
        description: 'Write down a quick note related to this submission.'
      }
    ] as IContextMenuActionEntry[]
  );

  return (
    <>
      <Flex
        gap={2}
        width="100%"
        backgroundColor="surfacePrimaryFill"
        paddingTop={0}
        justifyContent="flex-end"
      >
        {/* TODO: To be changed once we "connect" peer review and revision */}

        <ContextMenu actions={contextMenuActions} />
      </Flex>

      {/* ---- Add Note Modal ---- */}
      <AddNoteModal
        isOpen={!!showAddNoteModal}
        onClose={() => setShowAddNoteModal(null)}
        onAdd={() => setShowAddNoteModal(null)}
        isLoading={false}
        submissionId={submissionId}
        submissionContextId={submissionContextId}
      />

      <ExportSubmissionModal
        isOpen={showExportSubmissionModal}
        onClose={() => setExportSubmissionModal(false)}
        isLoading={false}
        submissionId={submissionId}
        submissionContextId={submissionContextId}
        phaseName={phaseName as 'preProduction' | 'formalAcceptance'}
      />

      <PrepareResponseToAuthorModal
        isOpen={isPrepareResponseModalOpen}
        onClose={() => setPrepareResponseModalOpen(false)}
        submissionId={submissionId}
      />
    </>
  );
};
